import useAPIClient from '@src/shared/hooks/api/useAPIClient';
import { useMutation } from '@src/shared/query';
import type { AppQueryKey } from '@src/shared/query/utils';
import { keepPreviousData, useQuery } from '@tanstack/react-query';

import {
  clientCardFromDto,
  clientDetailFromDto,
  clientGroupOptionFromDto,
  clientMatchFromDto,
  createClientFormData,
} from '../../lib';
import type {
  ClientDetail,
  ClientDetailDto,
  ClientMatch,
  ClientMatchDto,
  CreateClientPayload,
  GroupNameTakenDto,
  InternalNameParams,
  InternalNameProposal,
  InternalNameProposalDto,
} from '../../types';
import {
  clientGroupNameTakenUrl,
  clientInternalNameUrl,
  clientMatchesUrl,
  clientsUrl,
} from './constants';
import { CLIENTS_KEY, invalidateClients } from './keys';

const WIZARD_KEY: AppQueryKey = [...CLIENTS_KEY, 'wizard'];

/** The server lists matches from this many letters on. */
export const CLIENT_MATCH_MIN_LENGTH = 3;

/**
 * Clients of both segments whose legal or internal name resembles `text`,
 * best first ("Potentially Matching Clients"). Debounce the typed text.
 */
export function useClientMatches(text: string) {
  const apiClient = useAPIClient();
  const q = text.trim();
  const enabled = q.length >= CLIENT_MATCH_MIN_LENGTH;
  return useQuery({
    queryKey: [...WIZARD_KEY, 'matches', q],
    enabled,
    // Without the guard a too short text would keep showing old matches.
    placeholderData: enabled ? keepPreviousData : undefined,
    queryFn: ({ signal }) =>
      apiClient.request({
        url: clientMatchesUrl,
        payload: { q },
        options: { signal },
      }) as Promise<ClientMatchDto[]>,
    select: (data): ClientMatch[] => data.map(clientMatchFromDto),
  });
}

/**
 * The proposed internal name, and whether the typed one (or, without it,
 * the proposal) is taken in the segment. Debounce the typed values.
 */
export function useInternalNameProposal(
  { legalName, countryId, segment, name }: InternalNameParams,
  { enabled = true }: { enabled?: boolean } = {},
) {
  const apiClient = useAPIClient();
  const payload: Record<string, string> = { legal_name: legalName.trim() };
  if (countryId !== undefined) payload.country = String(countryId);
  if (segment) payload.segment = segment;
  if (name?.trim()) payload.name = name.trim();
  return useQuery({
    queryKey: [...WIZARD_KEY, 'internal-name', payload],
    enabled: enabled && Boolean(payload.legal_name || payload.name),
    placeholderData: keepPreviousData,
    queryFn: async ({ signal }): Promise<InternalNameProposal> => {
      const data = (await apiClient.request({
        url: clientInternalNameUrl,
        payload,
        options: { signal },
      })) as InternalNameProposalDto;
      return {
        proposal: data.proposal,
        taken: data.taken,
        existing: data.existing.map(clientCardFromDto),
      };
    },
  });
}

/**
 * The group of any segment named `name` already, ignoring case as the
 * server compares group names; null while it is free. Debounce the name.
 */
export function useGroupNameTaken(name: string) {
  const apiClient = useAPIClient();
  const trimmed = name.trim();
  return useQuery({
    queryKey: [...WIZARD_KEY, 'group-name-taken', trimmed],
    enabled: Boolean(trimmed),
    queryFn: async ({ signal }) => {
      const data = (await apiClient.request({
        url: clientGroupNameTakenUrl,
        payload: { name: trimmed },
        options: { signal },
      })) as GroupNameTakenDto;
      return data.taken && data.group
        ? clientGroupOptionFromDto(data.group)
        : null;
    },
  });
}

/**
 * Add Client wizard: creates the client with its group, head-office
 * address and contacts together. Errors come per section (`sections.group`,
 * `.general`, `.address`, `.contacts[index]`).
 */
export function useCreateClient() {
  return useMutation<ClientDetail, Error, CreateClientPayload>({
    mutationKey: [...WIZARD_KEY, 'create'],
    mutateFn: async ({ apiClient, ...payload }) =>
      clientDetailFromDto(
        (await apiClient.request({
          url: clientsUrl,
          method: 'POST',
          payload: createClientFormData(payload),
          contentType: 'multipart/form-data',
        })) as ClientDetailDto,
      ),
    onSuccess: () => {
      void invalidateClients();
    },
  });
}
